import EnumValueNotFoundException from '~/errors/EnumValueNotFoundException';

import { i18n } from '~/utils';
import { log } from '~/utils/logging';

import {
  API_DEFAULT_TIMEOUT_S,
  API_PDF_MODES,
  type ApiHeaderEntry,
  type ApiPdfMode,
  toApiHeaderEntries,
} from './apiContactPoint';
import {
  CONTACT_POINT_DATA_FORMAT,
  CONTACT_POINT_DATA_GROUPING,
  CONTACT_POINT_DIFFERENT_VALUES,
  CONTACT_POINT_INTEGRATION_TYPE,
} from './constants';
import {
  type DocuwareFieldMappingEntry,
  toDocuwareFieldMappingEntries,
} from './docuwareFieldMapping';
import {
  type ContactPoint,
  type ContactPointDataFormatKeys,
  type ContactPointDataGroupingKeys,
  type ContactPointIntegrationTypeKeys,
  type ContactPointOption,
} from './types';

/** Number (from the API) -> text-input value; absent numbers become ''. */
const numberToInput = (value: number | undefined) =>
  value == null ? '' : String(value);

/** Superscript digits -> plain digits, mirroring the backend's unit normalization. */
const SUPERSCRIPT_DIGITS: ReadonlyMap<string, string> = new Map([
  ['⁰', '0'],
  ['¹', '1'],
  ['²', '2'],
  ['³', '3'],
  ['⁴', '4'],
  ['⁵', '5'],
  ['⁶', '6'],
  ['⁷', '7'],
  ['⁸', '8'],
  ['⁹', '9'],
]);

/**
 * Ribpes uom-map units are restricted to letters + digits: the API client
 * snake-cases all JSON body keys, which would mangle other characters
 * (e.g. "M³" -> "m"). A superscript digit becomes its plain digit first
 * ("m³" -> "M3" — the backend matches units the same way), everything else
 * non-alphanumeric is dropped. Applied both to editor input and to map keys
 * loaded from the API (which may have been written by other clients).
 */
export const normalizeRibpesUnit = (value: string) =>
  [...value]
    .map((character) => SUPERSCRIPT_DIGITS.get(character) ?? character)
    .join('')
    .replaceAll(/[^0-9a-z]/gi, '')
    .toUpperCase();

/**
 * The SOFTLINE portal is addressed by its https origin only: scheme and host
 * (a port is fine), no path, query, fragment or credentials. Returns the
 * normalized origin (trimmed, lowercased scheme and host), or undefined when
 * the input is not such a URL. Validate before submission so invalid portal
 * URLs produce a field error instead of a failed request.
 */
export const parseSoftlineBaseUrl = (value: string): string | undefined => {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return undefined;
  }

  const isOrigin =
    url.protocol === 'https:' &&
    url.pathname === '/' &&
    !url.search &&
    !url.hash &&
    !url.username &&
    !url.password;

  return isOrigin ? url.origin : undefined;
};

const toApiPdfMode = (value: string | null | undefined): ApiPdfMode => {
  const mode = API_PDF_MODES.find((item) => item === value);
  if (value != null && !mode) {
    log.error({
      message: `Invalid api pdf mode: ${value}`,
      error: new EnumValueNotFoundException(`Invalid api pdf mode: ${value}`),
    });
  }

  return mode ?? 'none';
};

/** The timeout is kept as text like the SFTP port */
const createApiValues = (contactPoint: Partial<ContactPoint>) => ({
  apiAuthHeaderName: contactPoint.apiAuthHeaderName ?? '',
  // Secrets never come back, an empty one keeps the stored one
  apiAuthHeaderValue: '',
  apiAuthHeaderValueSet: contactPoint.apiAuthHeaderValueSet ?? false,
  apiHeaderEntries: toApiHeaderEntries(contactPoint.apiHeaders),
  apiPdfMode: toApiPdfMode(contactPoint.apiPdfMode),
  apiRemoveSigningSecret: false,
  apiSigningSecret: '',
  apiSigningSecretSet: contactPoint.apiSigningSecretSet ?? false,
  apiTimeoutS: numberToInput(contactPoint.apiTimeoutS ?? API_DEFAULT_TIMEOUT_S),
  apiUrl: contactPoint.apiUrl ?? '',
});

const toApiHeaderPairs = (entries: ApiHeaderEntry[] = []) =>
  JSON.stringify(entries.map(({ name, value }) => [name, value]));

const getApiHeaderChanges = (
  contactPointA: { apiHeaderEntries?: ApiHeaderEntry[] },
  contactPointB: { apiHeaderEntries?: ApiHeaderEntry[] },
) =>
  toApiHeaderPairs(contactPointA.apiHeaderEntries) ===
  toApiHeaderPairs(contactPointB.apiHeaderEntries)
    ? []
    : [
        i18n.t(($) => $.contactPoint.changedFields.apiHeaders, {
          ns: 'admin',
        }),
      ];

/** Initializes the DocuWare fields in contact point form state. */
const createDocuwareValues = (contactPoint: Partial<ContactPoint>) => ({
  docuwareAccessToken: contactPoint.docuwareAccessToken ?? '',
  docuwareAuthUrl: contactPoint.docuwareAuthUrl ?? '',
  docuwareBaseUrl: contactPoint.docuwareBaseUrl ?? '',
  docuwareClientId: contactPoint.docuwareClientId ?? '',
  docuwareClientSecret: contactPoint.docuwareClientSecret ?? '',
  // The mapping is edited as rows (id = stable React key) and folded back into
  // an object in the registry's toMutationBody.
  docuwareFieldMappingEntries: toDocuwareFieldMappingEntries(
    contactPoint.docuwareFieldMapping,
  ),
  docuwareFileCabinetId: contactPoint.docuwareFileCabinetId ?? '',
  docuwareOrganizationId: contactPoint.docuwareOrganizationId ?? '',
  docuwarePassword: contactPoint.docuwarePassword ?? '',
  // Minted by the API from the credentials above, never edited here. The form
  // saves with a full replace, so it has to hand them back unchanged or the
  // next upload would have to authenticate from scratch.
  docuwareRefreshToken: contactPoint.docuwareRefreshToken ?? '',
  docuwareStoreDialogId: contactPoint.docuwareStoreDialogId ?? '',
  docuwareTokenExpiresAt: contactPoint.docuwareTokenExpiresAt ?? '',
  docuwareUsername: contactPoint.docuwareUsername ?? '',
});
/** Mapping rows -> comparable value: the ordered rows without their row ids. */
const toDocuwareMappingPairs = (entries: DocuwareFieldMappingEntry[]) =>
  JSON.stringify(entries.map(({ id: _id, ...rest }) => rest));
/** An empty SFTP port is valid, the API then connects on port 22. */
export const isSftpPortInput = (value: string) =>
  value === '' ||
  (/^\d{1,5}$/.test(value) && Number(value) >= 1 && Number(value) <= 65_535);

/** SFTP connection values of the form state, the port kept as text like the RIB ids. */
const createSftpValues = (contactPoint: Partial<ContactPoint>) => ({
  ftpBaseDir: contactPoint.ftpBaseDir ?? '',
  // Never prefilled, so it is re-entered on edit like the SOFTLINE password.
  ftpPassword: '',
  ftpPort: numberToInput(contactPoint.ftpPort),
  ftpServer: contactPoint.ftpServer ?? '',
  ftpUsername: contactPoint.ftpUsername ?? '',
});

/** Uom rows -> comparable value: ordered (unit, uomId) pairs, row ids ignored. */
const toRibpesUomPairs = (entries: Array<{ unit: string; uomId: string }>) =>
  JSON.stringify(entries.map(({ unit, uomId }) => [unit, uomId]));

/** Resolves localized integration-specific change labels at call time for the active locale. */
const CHANGED_FIELD_LABELS: ReadonlyArray<[string, () => string]> = [
  [
    'apiAuthHeaderName',
    () =>
      i18n.t(($) => $.contactPoint.changedFields.apiAuthHeaderName, {
        ns: 'admin',
      }),
  ],
  [
    'apiAuthHeaderValue',
    () =>
      i18n.t(($) => $.contactPoint.changedFields.apiAuthHeaderValue, {
        ns: 'admin',
      }),
  ],
  [
    'apiPdfMode',
    () =>
      i18n.t(($) => $.contactPoint.changedFields.apiPdfMode, {
        ns: 'admin',
      }),
  ],
  [
    'apiRemoveSigningSecret',
    () =>
      i18n.t(($) => $.contactPoint.changedFields.apiSigningSecret, {
        ns: 'admin',
      }),
  ],
  [
    'apiSigningSecret',
    () =>
      i18n.t(($) => $.contactPoint.changedFields.apiSigningSecret, {
        ns: 'admin',
      }),
  ],
  [
    'apiTimeoutS',
    () =>
      i18n.t(($) => $.contactPoint.changedFields.apiTimeoutS, {
        ns: 'admin',
      }),
  ],
  [
    'apiUrl',
    () =>
      i18n.t(($) => $.contactPoint.changedFields.apiUrl, {
        ns: 'admin',
      }),
  ],
  [
    'azureQueueSasToken',
    () =>
      i18n.t(($) => $.contactPoint.changedFields.azureQueueSasToken, {
        ns: 'admin',
      }),
  ],
  [
    'azureQueueUrl',
    () =>
      i18n.t(($) => $.contactPoint.changedFields.azureQueueUrl, {
        ns: 'admin',
      }),
  ],
  [
    'docuwareAuthUrl',
    () =>
      i18n.t(($) => $.contactPoint.changedFields.docuwareAuthUrl, {
        ns: 'admin',
      }),
  ],
  [
    'docuwareBaseUrl',
    () =>
      i18n.t(($) => $.contactPoint.changedFields.docuwareBaseUrl, {
        ns: 'admin',
      }),
  ],
  [
    'docuwareClientId',
    () =>
      i18n.t(($) => $.contactPoint.changedFields.docuwareClientId, {
        ns: 'admin',
      }),
  ],
  [
    'docuwareClientSecret',
    () =>
      i18n.t(($) => $.contactPoint.changedFields.docuwareClientSecret, {
        ns: 'admin',
      }),
  ],
  [
    'docuwareFileCabinetId',
    () =>
      i18n.t(($) => $.contactPoint.changedFields.docuwareFileCabinetId, {
        ns: 'admin',
      }),
  ],
  [
    'docuwareOrganizationId',
    () =>
      i18n.t(($) => $.contactPoint.changedFields.docuwareOrganizationId, {
        ns: 'admin',
      }),
  ],
  [
    'docuwarePassword',
    () =>
      i18n.t(($) => $.contactPoint.changedFields.docuwarePassword, {
        ns: 'admin',
      }),
  ],
  [
    'docuwareStoreDialogId',
    () =>
      i18n.t(($) => $.contactPoint.changedFields.docuwareStoreDialogId, {
        ns: 'admin',
      }),
  ],
  [
    'docuwareUsername',
    () =>
      i18n.t(($) => $.contactPoint.changedFields.docuwareUsername, {
        ns: 'admin',
      }),
  ],
  [
    'ftpBaseDir',
    () =>
      i18n.t(($) => $.contactPoint.changedFields.ftpBaseDir, {
        ns: 'admin',
      }),
  ],
  [
    'ftpPort',
    () =>
      i18n.t(($) => $.contactPoint.changedFields.ftpPort, {
        ns: 'admin',
      }),
  ],
  [
    'ftpServer',
    () =>
      i18n.t(($) => $.contactPoint.changedFields.ftpServer, {
        ns: 'admin',
      }),
  ],
  [
    'ftpUsername',
    () =>
      i18n.t(($) => $.contactPoint.changedFields.ftpUsername, {
        ns: 'admin',
      }),
  ],
  [
    'ribpesBaseUrl',
    () =>
      i18n.t(($) => $.contactPoint.changedFields.ribpesBaseUrl, {
        ns: 'admin',
      }),
  ],
  [
    'ribpesBasSalesTaxMethodId',
    () =>
      i18n.t(($) => $.contactPoint.changedFields.ribpesBasSalesTaxMethodId, {
        ns: 'admin',
      }),
  ],
  [
    'ribpesClerkPrcId',
    () =>
      i18n.t(($) => $.contactPoint.changedFields.ribpesClerkPrcId, {
        ns: 'admin',
      }),
  ],
  [
    'ribpesCompanyCode',
    () =>
      i18n.t(($) => $.contactPoint.changedFields.ribpesCompanyCode, {
        ns: 'admin',
      }),
  ],
  [
    'ribpesCompanyId',
    () =>
      i18n.t(($) => $.contactPoint.changedFields.ribpesCompanyId, {
        ns: 'admin',
      }),
  ],
  [
    'ribpesPesStatusId',
    () =>
      i18n.t(($) => $.contactPoint.changedFields.ribpesPesStatusId, {
        ns: 'admin',
      }),
  ],
  [
    'ribpesPrcConfigurationId',
    () =>
      i18n.t(($) => $.contactPoint.changedFields.ribpesPrcConfigurationId, {
        ns: 'admin',
      }),
  ],
  [
    'ribpesPrjDocumentTypeId',
    () =>
      i18n.t(($) => $.contactPoint.changedFields.ribpesPrjDocumentTypeId, {
        ns: 'admin',
      }),
  ],
  [
    'ribpesRoleId',
    () =>
      i18n.t(($) => $.contactPoint.changedFields.ribpesRoleId, {
        ns: 'admin',
      }),
  ],
  [
    'ribpesUsername',
    () =>
      i18n.t(($) => $.contactPoint.changedFields.ribpesUsername, {
        ns: 'admin',
      }),
  ],
  [
    'softlineBaseUrl',
    () =>
      i18n.t(($) => $.contactPoint.changedFields.softlineBaseUrl, {
        ns: 'admin',
      }),
  ],
  [
    'softlineUsername',
    () =>
      i18n.t(($) => $.contactPoint.changedFields.softlineUsername, {
        ns: 'admin',
      }),
  ],
];

/**
 * Narrows the loosely-typed contact-point query result to the id/name pairs used
 * by the execution-log recipient filter and name lookups, centralising the one
 * unavoidable cast (the query hook returns `unknown[] | ContactPoint[]`).
 */
export const toContactPointOptions = (
  contactPoints: unknown,
): ContactPointOption[] =>
  ((contactPoints as ContactPointOption[] | null | undefined) ?? []).map(
    ({ id, name }) => ({ id, name }),
  );

/**
 * Represents a utility object for managing contact points.
 */
export const ContactPointObject = {
  /**
   * Creates a new contact point object.
   * @param contactPoint - An object containing the properties of a contact point.
   * @returns A fully initialized contact point object.
   */
  create(contactPoint: Partial<ContactPoint> = {}) {
    return {
      ...createApiValues(contactPoint),
      azureQueueSasToken: contactPoint.azureQueueSasToken ?? '',
      azureQueueUrl: contactPoint.azureQueueUrl ?? '',
      companyAccountId: contactPoint.companyAccountId ?? '',
      dataFormat: contactPoint.dataFormat ?? this.DATA_FORMAT.ZIP.KEY,
      dataGrouping:
        contactPoint.dataGrouping ?? this.DATA_GROUPING.NO_GROUPING.KEY,
      dlnFilenamePlaceholder: contactPoint.dlnFilenamePlaceholder ?? '',
      doctype: contactPoint.doctype ?? 'docx',
      ...createDocuwareValues(contactPoint),
      emailReceivers: contactPoint.emailReceivers ?? [],
      emailSubject: contactPoint.emailSubject ?? '',
      ...createSftpValues(contactPoint),
      id: contactPoint.id ?? '',
      includeAttachments: contactPoint.includeAttachments ?? true,
      integrationType:
        contactPoint.integrationType ?? this.INTEGRATION_TYPE.EMAIL.KEY,
      name: contactPoint.name ?? '',
      ribpesBaseUrl: contactPoint.ribpesBaseUrl ?? '',
      // Numeric RIB FK ids are kept as strings in the form state (text inputs)
      // and parsed back to numbers in the registry's toMutationBody.
      ribpesBasSalesTaxMethodId: numberToInput(
        contactPoint.ribpesBasSalesTaxMethodId,
      ),
      ribpesClerkPrcId: numberToInput(contactPoint.ribpesClerkPrcId),
      ribpesCompanyCode: contactPoint.ribpesCompanyCode ?? '',
      ribpesCompanyId: numberToInput(contactPoint.ribpesCompanyId),
      // Like brz8Password, the password is never returned by the API, so editing
      // requires re-entering it.
      ribpesPassword: contactPoint.ribpesPassword ?? '',
      ribpesPesStatusId: numberToInput(contactPoint.ribpesPesStatusId),
      ribpesPrcConfigurationId: numberToInput(
        contactPoint.ribpesPrcConfigurationId,
      ),
      ribpesPrjDocumentTypeId: numberToInput(
        contactPoint.ribpesPrjDocumentTypeId,
      ),
      ribpesRoleId: numberToInput(contactPoint.ribpesRoleId),
      // The unit map is edited as rows (id = stable React key); converted back
      // to a map in toMutationBody.
      ribpesUomEntries: Object.entries(contactPoint.ribpesUomMap ?? {}).map(
        ([unit, uomId]) => ({
          id: crypto.randomUUID(),
          unit: normalizeRibpesUnit(unit),
          uomId: String(uomId),
        }),
      ),
      ribpesUsername: contactPoint.ribpesUsername ?? '',
      softlineBaseUrl: contactPoint.softlineBaseUrl ?? '',
      // The API never returns the password, so the form always starts empty
      // and it is re-entered on edit.
      softlinePassword: '',
      softlineUsername: contactPoint.softlineUsername ?? '',
    };
  },

  DATA_FORMAT: CONTACT_POINT_DATA_FORMAT,

  DATA_GROUPING: CONTACT_POINT_DATA_GROUPING,

  /**
   * Gets the string representation of a data format by its key.
   * @param key - The key of the data format.
   * @returns The string representation of the data format.
   */
  getDataFormatString(key: string) {
    const dataFormatKey = Object.keys(this.DATA_FORMAT).find(
      (x) => this.DATA_FORMAT[x as ContactPointDataFormatKeys].KEY === key,
    );

    if (!dataFormatKey) {
      log.error({
        message: `Invalid data format: ${key}`,
        error: new EnumValueNotFoundException(`Invalid data format: ${key}`),
      });
      return key;
    }

    return this.DATA_FORMAT[dataFormatKey as ContactPointDataFormatKeys].STRING;
  },

  /**
   * Gets the string representation of a data grouping by its key.
   * @param key - The key of the data grouping.
   * @returns The string representation of the data grouping.
   */
  getDataGroupingString(key: string) {
    const dataGroupingKey = Object.keys(this.DATA_GROUPING).find(
      (x) => this.DATA_GROUPING[x as ContactPointDataGroupingKeys].KEY === key,
    );

    if (!dataGroupingKey) {
      log.error({
        message: `Invalid data grouping: ${key}`,
        error: new EnumValueNotFoundException(`Invalid data grouping: ${key}`),
      });
      return key;
    }

    return this.DATA_GROUPING[dataGroupingKey as ContactPointDataGroupingKeys]
      .STRING;
  },

  /**
   * Compares two contact points and returns their differences.
   * @param contactPointA - The first contact point.
   * @param contactPointB - The second contact point.
   * @returns An array of strings representing the differing properties.
   */
  getDifferences(
    contactPointA: Record<string, any>,
    contactPointB: Record<string, any>,
  ) {
    const differentValues: string[] = [];

    if (contactPointA.id !== contactPointB.id)
      differentValues.push(CONTACT_POINT_DIFFERENT_VALUES.ID);
    if (contactPointA.companyAccountId !== contactPointB.companyAccountId)
      differentValues.push(CONTACT_POINT_DIFFERENT_VALUES.COMPANY_ACCOUNT_ID);
    if (contactPointA.name !== contactPointB.name)
      differentValues.push(CONTACT_POINT_DIFFERENT_VALUES.NAME);
    if (contactPointA.integrationType !== contactPointB.integrationType)
      differentValues.push(CONTACT_POINT_DIFFERENT_VALUES.INTEGRATION_TYPE);
    if (
      JSON.stringify(contactPointA.emailReceivers) !==
      JSON.stringify(contactPointB.emailReceivers)
    )
      differentValues.push(CONTACT_POINT_DIFFERENT_VALUES.EMAIL_RECEIVERS);
    if (
      contactPointA.dlnFilenamePlaceholder !==
      contactPointB.dlnFilenamePlaceholder
    )
      differentValues.push(
        CONTACT_POINT_DIFFERENT_VALUES.DLN_FILENAME_PLACEHOLDER,
      );
    if (contactPointA.emailSubject !== contactPointB.emailSubject)
      differentValues.push(CONTACT_POINT_DIFFERENT_VALUES.EMAIL_SUBJECT);
    if (contactPointA.dataFormat !== contactPointB.dataFormat)
      differentValues.push(CONTACT_POINT_DIFFERENT_VALUES.DATA_FORMAT);
    if (contactPointA.dataGrouping !== contactPointB.dataGrouping)
      differentValues.push(CONTACT_POINT_DIFFERENT_VALUES.DATA_GROUPING);
    if (contactPointA.doctype !== contactPointB.doctype)
      differentValues.push(CONTACT_POINT_DIFFERENT_VALUES.DOCTYPE);
    if (contactPointA.includeAttachments !== contactPointB.includeAttachments)
      differentValues.push(CONTACT_POINT_DIFFERENT_VALUES.INCLUDES_ATTACHMENTS);

    // Password fields are deliberately not compared: the API never returns
    // them, so re-entering one on edit is a save requirement, not a user change.
    // The API auth header value and signing secret are compared, a typed one is a real change
    // The DocuWare password and client secret are compared: the form keeps the
    // stored values, so an edit to one is a real change the user can lose.
    // Only the label is listed, never the value.
    // softlineBaseUrl is compared as typed: a cosmetic retype (trailing slash,
    // whitespace) counts as a change although the same origin is sent.
    for (const [field, getLabel] of CHANGED_FIELD_LABELS) {
      if (contactPointA[field] !== contactPointB[field]) {
        differentValues.push(getLabel());
      }
    }

    if (
      toDocuwareMappingPairs(
        contactPointA.docuwareFieldMappingEntries ?? [],
      ) !==
      toDocuwareMappingPairs(contactPointB.docuwareFieldMappingEntries ?? [])
    ) {
      differentValues.push(
        i18n.t(($) => $.contactPoint.changedFields.docuwareFieldMapping, {
          ns: 'admin',
        }),
      );
    }

    differentValues.push(...getApiHeaderChanges(contactPointA, contactPointB));

    if (
      toRibpesUomPairs(contactPointA.ribpesUomEntries ?? []) !==
      toRibpesUomPairs(contactPointB.ribpesUomEntries ?? [])
    ) {
      differentValues.push(
        i18n.t(($) => $.contactPoint.changedFields.ribpesUomMap, {
          ns: 'admin',
        }),
      );
    }

    return differentValues;
  },

  /**
   * Gets the string representation of an integration type by its key.
   * @param key - The key of the integration type.
   * @returns The string representation of the integration type.
   */
  getIntegrationTypeString(key: string) {
    const integrationTypeKey = Object.keys(this.INTEGRATION_TYPE).find(
      (x) =>
        this.INTEGRATION_TYPE[x as ContactPointIntegrationTypeKeys].KEY === key,
    );

    if (!integrationTypeKey) {
      log.error({
        message: `Invalid integration type: ${key}`,
        error: new EnumValueNotFoundException(
          `Invalid integration type: ${key}`,
        ),
      });
      return key;
    }

    return this.INTEGRATION_TYPE[
      integrationTypeKey as ContactPointIntegrationTypeKeys
    ].STRING;
  },

  INTEGRATION_TYPE: CONTACT_POINT_INTEGRATION_TYPE,

  /**
   * Validates a contact point object.
   * @param contactPoint - The contact point to validate.
   * @returns A tuple where the first element indicates validity and the second element is an error message if invalid.
   */
  isValid(contactPoint: Record<string, any>) {
    if (
      contactPoint.dataGrouping === this.DATA_GROUPING.COMBINED.KEY &&
      contactPoint.dlnFilenamePlaceholder
    ) {
      return [
        false,
        'Dateiname darf nicht gesetzt sein, wenn Lieferscheine gruppiert werden.',
      ];
    }

    return [true, null];
  },
};
